import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { STATUS_STYLES } from "../constants";
import { JobOverview } from "./JobOverview";
import { JobServices } from "./JobServices";
import { JobProperty } from "./JobProperty";
import { JobGalleries } from "./JobGalleries";
import { JobInvoice } from "./JobInvoice";

export function JobDialog({ bookingId, photographers, initialTab = "overview", onClose, onChanged }) {
  const [job, setJob] = useState(null);
  const reload = useCallback(() => api.get(`/admin/jobs/${bookingId}`).then((r) => setJob(r.data)).catch(() => {}), [bookingId]);
  useEffect(() => { reload(); }, [reload]);
  const changed = () => { reload(); onChanged(); };

  const tabCls = "rounded-lg data-[state=active]:bg-[#0C2347] data-[state=active]:text-white font-mono text-[11px] uppercase tracking-[0.1em] text-[#475467]";
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-6xl w-[96vw] h-[92vh] rounded-xl p-0 flex flex-col overflow-hidden" data-testid="job-dialog">
        <DialogTitle className="sr-only">Order details</DialogTitle><DialogDescription className="sr-only">Manage the selected order, services, property, gallery, and invoice.</DialogDescription>
        {job && (
          <>
            <div className="px-6 md:px-8 pt-6 pb-4 border-b border-[#E4E9F0] flex flex-wrap items-end justify-between gap-4">
              <div>
                <div className="flex items-center gap-3">
                  <span className="font-mono text-sm text-[#475467]">#{job.job_number || job.reference}</span>
                  <span className={`text-[11px] font-medium px-2.5 py-1 ${STATUS_STYLES[job.status] || "rounded-full bg-[#F1F2F4] text-[#51565F]"}`}>{job.status}</span>
                  {job.archived && <span className="text-[11px] font-medium px-2.5 py-1 rounded-full bg-[#F1F2F4] text-[#94A3B8]">Archived</span>}
                </div>
                <h2 className="font-serif-display text-2xl md:text-3xl mt-2 text-[#101828]">{job.address.street}, {job.address.city}</h2>
                <div className="text-sm text-[#475467] mt-1">{job.contact.name} · {job.contact.email}{job.contact.phone ? ` · ${job.contact.phone}` : ""}</div>
              </div>
              <div className="text-right"><div className="eyebrow">Total</div><div className="font-serif-display text-3xl text-[#101828]">${job.quote.total}</div></div>
            </div>
            <Tabs defaultValue={initialTab} className="flex-1 flex flex-col min-h-0">
              <TabsList className="mx-6 md:mx-8 mt-4 bg-[#F7F9FC] border border-[#E4E9F0] rounded-xl p-1 h-auto flex-wrap self-start">
                <TabsTrigger value="overview" data-testid="job-tab-overview" className={tabCls}>Overview</TabsTrigger>
                <TabsTrigger value="services" data-testid="job-tab-services" className={tabCls}>Services</TabsTrigger>
                <TabsTrigger value="property" data-testid="job-tab-property" className={tabCls}>Property</TabsTrigger>
                <TabsTrigger value="galleries" data-testid="job-tab-galleries" className={tabCls}>Galleries ({job.files?.length || 0})</TabsTrigger>
                <TabsTrigger value="invoice" data-testid="job-tab-invoice" className={tabCls}>Invoice</TabsTrigger>
              </TabsList>
              <div className="flex-1 overflow-y-auto px-6 md:px-8 py-6">
                <TabsContent value="overview" className="mt-0"><JobOverview job={job} photographers={photographers} onChanged={changed} onClose={onClose} /></TabsContent>
                <TabsContent value="services" className="mt-0"><JobServices job={job} photographers={photographers} onChanged={changed} /></TabsContent>
                <TabsContent value="property" className="mt-0"><JobProperty job={job} onChanged={changed} /></TabsContent>
                <TabsContent value="galleries" className="mt-0"><JobGalleries job={job} onChanged={changed} /></TabsContent>
                <TabsContent value="invoice" className="mt-0"><JobInvoice job={job} onChanged={changed} /></TabsContent>
              </div>
            </Tabs>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
